import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import api from '../services/apiService';

const USAGE_AREA = 'orderNotes';

const emptyBlock = {
  name: '',
  content: '',
  sortOrder: '',
  isActive: true,
};

const fieldStyle = {
  width: '100%',
  padding: 8,
  border: '1px solid #ccc',
  borderRadius: 4,
  boxSizing: 'border-box',
  font: 'inherit',
};

const buttonStyle = {
  padding: '8px 14px',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const normalizeForApi = (block) => ({
  name: String(block.name || '').trim(),
  content: String(block.content || '').trim(),
  usageArea: USAGE_AREA,
  sortOrder: block.sortOrder === '' ? 0 : Number.parseInt(block.sortOrder, 10),
  isActive: block.isActive !== false,
});

const TextBlockForm = () => {
  const [textBlocks, setTextBlocks] = useState([]);
  const [newBlock, setNewBlock] = useState(emptyBlock);
  const [editingId, setEditingId] = useState(null);
  const [editedBlock, setEditedBlock] = useState(emptyBlock);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadTextBlocks = useCallback(async () => {
    try {
      const response = await api.get('/text-blocks', {
        params: { usageArea: USAGE_AREA },
      });
      setTextBlocks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Fehler beim Laden der Textbausteine:', error);
      alert('Fehler beim Laden der Textbausteine.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTextBlocks();
  }, [loadTextBlocks]);

  const setNewField = (field, value) => {
    setNewBlock(prev => ({ ...prev, [field]: value }));
  };

  const setEditedField = (field, value) => {
    setEditedBlock(prev => ({ ...prev, [field]: value }));
  };

  const validate = (block) => {
    if (!String(block.name || '').trim()) {
      alert('Bitte einen Namen für den Textbaustein eingeben.');
      return false;
    }
    if (!String(block.content || '').trim()) {
      alert('Bitte einen Inhalt für den Textbaustein eingeben.');
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (!validate(newBlock)) return;

    setSaving(true);
    try {
      await api.post('/text-blocks', normalizeForApi(newBlock));
      setNewBlock(emptyBlock);
      await loadTextBlocks();
    } catch (error) {
      console.error('Fehler beim Anlegen des Textbausteins:', error);
      alert(error.response?.data?.error || 'Fehler beim Anlegen des Textbausteins.');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (textBlock) => {
    setEditingId(textBlock.id);
    setEditedBlock({
      name: textBlock.name || '',
      content: textBlock.content || '',
      sortOrder: textBlock.sortOrder ?? '',
      isActive: textBlock.isActive !== false,
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditedBlock(emptyBlock);
  };

  const handleUpdate = async (id) => {
    if (!validate(editedBlock)) return;

    setSaving(true);
    try {
      await api.put(`/text-blocks/${id}`, normalizeForApi(editedBlock));
      cancelEditing();
      await loadTextBlocks();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Textbausteins:', error);
      alert(error.response?.data?.error || 'Fehler beim Aktualisieren des Textbausteins.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Textbaustein wirklich löschen? Bereits eingefügte Angebots- und Auftragstexte bleiben erhalten.')) return;

    try {
      await api.delete(`/text-blocks/${id}`);
      if (editingId === id) cancelEditing();
      await loadTextBlocks();
    } catch (error) {
      console.error('Fehler beim Löschen des Textbausteins:', error);
      alert(error.response?.data?.error || 'Fehler beim Löschen des Textbausteins.');
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: 'min(100%, 980px)' }}>
        <Helmet>
          <title>Textbausteine verwalten</title>
          <meta name="description" content="Textbausteine für Angebots- und Auftragsnotizen verwalten" />
        </Helmet>

        <h2 style={{ textAlign: 'center', marginBottom: 20 }}>Textbausteine für Angebots- und Auftragsnotizen</h2>

        <div style={{ padding: 16, border: '1px solid #ddd', borderRadius: 6, marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 120px', gap: 12, marginBottom: 12 }}>
            <label>
              Name
              <input
                type="text"
                value={newBlock.name}
                onChange={(event) => setNewField('name', event.target.value)}
                maxLength={120}
                style={{ ...fieldStyle, marginTop: 4 }}
                placeholder="Bezeichnung im Dropdown"
              />
            </label>
            <label>
              Sortierung
              <input
                type="number"
                value={newBlock.sortOrder}
                onChange={(event) => setNewField('sortOrder', event.target.value)}
                style={{ ...fieldStyle, marginTop: 4 }}
                placeholder="0"
              />
            </label>
          </div>

          <label style={{ display: 'block', marginBottom: 12 }}>
            Text
            <textarea
              value={newBlock.content}
              onChange={(event) => setNewField('content', event.target.value)}
              rows={6}
              style={{ ...fieldStyle, marginTop: 4, resize: 'vertical', whiteSpace: 'pre-wrap' }}
              placeholder="Textbaustein eingeben"
            />
          </label>

          <button
            type="button"
            onClick={handleAdd}
            disabled={saving}
            style={{ ...buttonStyle, background: '#28a745', color: '#fff', opacity: saving ? 0.65 : 1 }}
          >
            Textbaustein hinzufügen
          </button>
        </div>

        {loading ? (
          <p>Daten werden geladen...</p>
        ) : textBlocks.length === 0 ? (
          <p>Es sind noch keine Textbausteine vorhanden.</p>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {textBlocks.map(textBlock => (
              <div key={textBlock.id} style={{ padding: 16, border: '1px solid #ddd', borderRadius: 6 }}>
                {editingId === textBlock.id ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 120px', gap: 12, marginBottom: 12 }}>
                      <label>
                        Name
                        <input
                          type="text"
                          value={editedBlock.name}
                          onChange={(event) => setEditedField('name', event.target.value)}
                          maxLength={120}
                          style={{ ...fieldStyle, marginTop: 4 }}
                        />
                      </label>
                      <label>
                        Sortierung
                        <input
                          type="number"
                          value={editedBlock.sortOrder}
                          onChange={(event) => setEditedField('sortOrder', event.target.value)}
                          style={{ ...fieldStyle, marginTop: 4 }}
                        />
                      </label>
                    </div>

                    <label style={{ display: 'block', marginBottom: 12 }}>
                      Text
                      <textarea
                        value={editedBlock.content}
                        onChange={(event) => setEditedField('content', event.target.value)}
                        rows={6}
                        style={{ ...fieldStyle, marginTop: 4, resize: 'vertical', whiteSpace: 'pre-wrap' }}
                      />
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                      <input
                        type="checkbox"
                        checked={editedBlock.isActive}
                        onChange={(event) => setEditedField('isActive', event.target.checked)}
                      />
                      Aktiv und in der Angebots- und Auftragsbearbeitung auswählbar
                    </label>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={() => handleUpdate(textBlock.id)} disabled={saving} style={{ ...buttonStyle, background: '#007bff', color: '#fff' }}>
                        Speichern
                      </button>
                      <button type="button" onClick={cancelEditing} disabled={saving} style={{ ...buttonStyle, background: '#6c757d', color: '#fff' }}>
                        Abbrechen
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                      <div>
                        <strong>{textBlock.name}</strong>
                        <span style={{ marginLeft: 10, color: textBlock.isActive ? '#2e7d32' : '#777' }}>
                          {textBlock.isActive ? 'Aktiv' : 'Inaktiv'} · Sortierung {textBlock.sortOrder}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => startEditing(textBlock)} style={{ ...buttonStyle, background: '#007bff', color: '#fff' }}>
                          Bearbeiten
                        </button>
                        <button type="button" onClick={() => handleDelete(textBlock.id)} style={{ ...buttonStyle, background: '#dc3545', color: '#fff' }}>
                          Löschen
                        </button>
                      </div>
                    </div>
                    <div style={{ marginTop: 12, whiteSpace: 'pre-wrap', lineHeight: 1.4, overflowWrap: 'anywhere' }}>
                      {textBlock.content}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TextBlockForm;
